// icons.jsx — minimal stroke icon set (Lucide-inspired, original)
import React from 'react';

const Ic = {};
// Default width/height=20 and flex:none so an icon is NEVER unbounded. Without a size a bare <svg>
// stretches to its container — which is how a stray icon in an inline-styled flex box (one with no
// `.x svg{width…}` CSS rule, e.g. an alert banner) rendered as a giant triangle. These are plain
// presentation attributes / style, so any CSS rule (.btn svg, .nav-item svg, …) still overrides the
// size, and an explicit prop (width=…/style=…) still wins because {...props} is spread last.
const make = (path) => ({ style, ...props } = {}) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: "none", ...style }}
    {...props}
  >
    {path}
  </svg>
);

Ic.dashboard = make(
  <>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </>
);
Ic.catalog = make(
  <>
    <path d="M4 5h12a2 2 0 012 2v13H6a2 2 0 01-2-2V5z" />
    <path d="M4 5a2 2 0 012-2h12" />
    <path d="M8 8h8M8 12h6" />
  </>
);
Ic.selector = make(
  <>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 4v3M12 17v3M4 12h3M17 12h3" />
    <circle cx="12" cy="12" r="2.5" />
  </>
);
Ic.commercial = make(
  <>
    <path d="M3 7h18l-2 12H5L3 7z" />
    <path d="M8 7V5a4 4 0 018 0v2" />
  </>
);
Ic.finance = make(
  <>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18" />
    <path d="M7 15h4" />
  </>
);
Ic.contract = make(
  <>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
    <path d="M8 13h8M8 17h5" />
  </>
);
Ic.materials = make(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M21 17l-5-5-7 7" />
  </>
);
Ic.service = make(
  <>
    <path d="M14.7 6.3a1 1 0 010 1.4l-9 9a1 1 0 01-1.4 0l-1-1a1 1 0 010-1.4l9-9a1 1 0 011.4 0l1 1z" />
    <path d="M16 4l4 4M19 11l2 2-3 3-2-2" />
  </>
);
Ic.news = make(
  <>
    <path d="M4 4h12v16H6a2 2 0 01-2-2V4z" />
    <path d="M16 8h3a1 1 0 011 1v9a2 2 0 01-2 2" />
    <path d="M7 8h6M7 12h6M7 16h4" />
  </>
);
Ic.search = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </>
);
Ic.bell = make(
  <>
    <path d="M6 8a6 6 0 0112 0c0 7 3 8 3 8H3s3-1 3-8z" />
    <path d="M10 21a2 2 0 004 0" />
  </>
);
Ic.help = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4" />
    <circle cx="12" cy="17" r=".6" fill="currentColor" />
  </>
);
Ic.info = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <circle cx="12" cy="8" r=".6" fill="currentColor" />
  </>
);
Ic.chev = make(<path d="M9 6l6 6-6 6" />);
Ic.chevDown = make(<path d="M6 9l6 6 6-6" />);
Ic.plus = make(
  <>
    <path d="M12 5v14M5 12h14" />
  </>
);
Ic.download = make(
  <>
    <path d="M12 4v12M6 10l6 6 6-6" />
    <path d="M4 20h16" />
  </>
);
Ic.upload = make(
  <>
    <path d="M12 20V8M6 14l6-6 6 6" />
    <path d="M4 4h16" />
  </>
);
Ic.filter = make(<path d="M4 5h16l-6 8v6l-4-2v-4z" />);
Ic.grid = make(
  <>
    <rect x="4" y="4" width="7" height="7" rx="1" />
    <rect x="13" y="4" width="7" height="7" rx="1" />
    <rect x="4" y="13" width="7" height="7" rx="1" />
    <rect x="13" y="13" width="7" height="7" rx="1" />
  </>
);
Ic.list = make(
  <>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </>
);
Ic.arrowUp = make(<path d="M12 19V5M5 12l7-7 7 7" />);
Ic.arrowDown = make(<path d="M12 5v14M5 12l7 7 7-7" />);
Ic.check = make(<path d="M5 12l5 5L20 7" />);
Ic.x = make(<path d="M6 6l12 12M18 6L6 18" />);
Ic.eye = make(
  <>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </>
);
Ic.dl = Ic.download;
Ic.print = make(
  <>
    <path d="M6 9V3h12v6" />
    <rect x="4" y="9" width="16" height="8" rx="1" />
    <path d="M7 17v4h10v-4" />
  </>
);
Ic.copy = make(
  <>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3" />
  </>
);
Ic.send = make(<path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />);
Ic.play = make(<path d="M8 5l12 7-12 7V5z" fill="currentColor" />);
Ic.pdf = make(
  <>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M14 3v6h6" />
  </>
);
Ic.image = make(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M21 17l-5-5-7 7" />
  </>
);
Ic.zip = make(
  <>
    <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" />
    <path d="M10 4v3M10 9v2M10 13v2M10 17v2" />
  </>
);
Ic.brand = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3a9 9 0 010 18M3 12h18" />
  </>
);
Ic.world = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a13 13 0 010 18M12 3a13 13 0 000 18" />
  </>
);
Ic.calendar = make(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </>
);
Ic.user = make(
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0116 0" />
  </>
);
// A group, not a person: Ic.user already carries "one account" all over the admin screens, while
// the sales-team entry is about the people as a set.
//
// Second attempt. The first drew the rear figure's head as an open arc and let the two bodies run
// together; rendered at 20px it read as a blob with a comma beside it, and the owner said so. The
// lesson is not about SVG: I had written the coordinates and shipped them without ever looking at
// the result. This version was drawn, rendered, and compared against two alternatives before it
// was chosen — a full second head and flat shoulders keep both figures legible at 20px.
Ic.team = make(
  <>
    <circle cx="9" cy="7.5" r="3.2" />
    <path d="M3 20v-1a6 6 0 0 1 12 0v1" />
    <circle cx="17.2" cy="6.8" r="2.4" />
    <path d="M21 20v-1a5 5 0 0 0-3.4-4.7" />
  </>
);
Ic.factory = make(
  <>
    <path d="M3 21V10l5 3V10l5 3V8l8 5v8H3z" />
    <path d="M8 21v-4M13 21v-4M18 21v-4" />
  </>
);
Ic.fire = make(
  <>
    <path d="M12 3s5 4 5 9a5 5 0 01-10 0c0-2 1-3 1-3s2 1 2 3c0-3 2-5 2-9z" />
  </>
);
Ic.drop = make(
  <>
    <path d="M12 3s7 7 7 12a7 7 0 01-14 0c0-5 7-12 7-12z" />
  </>
);
Ic.bolt = make(<path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />);
Ic.tools = make(
  <>
    <path d="M14 6l4 4-9 9-4 1 1-4 9-9z" />
    <path d="M16 8l4-4-2-2-4 4" />
  </>
);
Ic.book = make(
  <>
    <path d="M4 5a2 2 0 012-2h13v18H6a2 2 0 01-2-2V5z" />
    <path d="M19 16H6a2 2 0 00-2 2" />
  </>
);
Ic.cog = make(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
  </>
);
Ic.menu = make(<path d="M4 6h16M4 12h16M4 18h16" />);
Ic.arrowRight = make(<path d="M5 12h14M13 5l7 7-7 7" />);
Ic.refresh = make(
  <>
    <path d="M21 12a9 9 0 11-3-6.7" />
    <path d="M21 4v5h-5" />
  </>
);
Ic.alert = make(
  <>
    <path d="M12 3l10 18H2L12 3z" />
    <path d="M12 10v5" />
    <circle cx="12" cy="18" r=".6" fill="currentColor" />
  </>
);
Ic.trend = make(
  <>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </>
);
Ic.box = make(
  <>
    <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </>
);
Ic.tag = make(
  <>
    <path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 13V3h10l7.6 7.6a2 2 0 010 2.8z" />
    <circle cx="7.5" cy="7.5" r="1.3" />
  </>
);
Ic.target = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1.3" />
  </>
);
Ic.briefcase = make(
  <>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2M3 13h18" />
  </>
);
Ic.truck = make(
  <>
    <rect x="2" y="7" width="12" height="9" rx="1" />
    <path d="M14 10h4l3 3v3h-7" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
  </>
);
Ic.handshake = make(
  <>
    <path d="M2 12l4-4 5 4-3 3-3-1-3 2z" />
    <path d="M22 12l-4-4-5 4 3 3 3-1 3 2z" />
    <path d="M11 12l1 1 2-2" />
  </>
);
Ic.chat = make(
  <>
    <path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 21l2-5.5A8.5 8.5 0 1 1 21 11.5z" />
    <path d="M8 11h8M8 14.5h5" />
  </>
);

export default Ic;
